import { Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import NitrogenSmartFloatingButton from '/@/components/nitrogen-smart/buttons/floating-button';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { useGetHistoricalWeather } from '/@/hooks/weather';
import { _ } from '/@/locale';
import { setCropProtectionProducts } from '/@/store/crop-monitoring/actions';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance, getNitrogenSmartEnabled } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import CreateRecommendationModal from '/@/views/dashboard/rv/components/create-recommendation-modal';
import FieldMonitoringDetail from '../../../components/detail';

Date.prototype.subtractDays = function (days) {
    const date = new Date(this.valueOf());
    date.setDate(date.getDate() - days);
    return date;
};

Date.prototype.addDays = function (days) {
    const date = new Date(this.valueOf());
    date.setDate(date.getDate() + days);
    return date;
};

const FieldMonitoringDashboardCurrentCampaign = ({
    cropMonitoringId,
    fieldId,
    cropMonitoring,
    isCMLoading,
}: {
    cropMonitoringId?: number;
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    isCMLoading: boolean;
}) => {
    const dispatch = useDispatch();
    const setCPProducts = bindActionCreators(setCropProtectionProducts, dispatch);
    const appInstance = useSelector(getAppInstance);
    const [showCropProtection, setShowCropProtection] = useState(false);
    const [newRecommendationModalOpened, setNewRecommendationModalOpened] = useState(false);
    const [technology, setTechnology] = useState('VTPRO3');
    const nSmartEnabled = useSelector(getNitrogenSmartEnabled);

    const fieldQuery = useFieldById(
        { id: fieldId || cropMonitoring?.field || 0 },
        { enabled: Boolean(fieldId || cropMonitoring?.field) }
    );

    const weatherCropMonitoringId = cropMonitoring?.id ?? cropMonitoringId;
    const { data: historicalWeather, loading: loadingWeather } = useGetHistoricalWeather(
        weatherCropMonitoringId ?? 0,
        {},
        0,
        { enabled: Boolean(weatherCropMonitoringId) }
    );

    let showAlert: boolean;
    if (cropMonitoring) {
        let toDate = new Date(cropMonitoring.expectedFloweringDate);
        toDate = toDate.addDays(16);
        const today = new Date();
        showAlert = toDate >= today;
    }

    const handleShowCropProtection = (state) => {
        dispatch(trackEvent({ key: 'cornCropMonitoring.detail.cropProtectionSwitch', state }));
        setShowCropProtection(state);
    };

    useEffect(() => {
        axFetch
            .get(
                `${settings.urls.apiDomain}/core/v1/crop-monitoring/crop-protection-products-recommendation/?app_instance_id=${appInstance.id}`
            )
            .then((res) => setCPProducts(res.data));
    }, []);

    const references = [
        { text: `${_('Siembra')}`, color: '#97723A' },
        { text: `${_('Emergencia')} - ${_('Vegetativo')}`, color: '#9AD15E' },
        { text: `${_('Floración')} ${_('Madurez')}`, color: '#006A28' },
        { text: `${_('Cosecha')}`, color: '#EED21D' },
    ];

    let showNSmartButton = false;
    const cropData = getCropMonitoringData(cropMonitoring);
    if (cropMonitoring && nSmartEnabled) {
        if (
            cropData?.phenology === 'emergence' &&
            cropData?.vegetativeStage < 6 &&
            fieldQuery.data?.geometry.length > 1
        ) {
            showNSmartButton = true;
        }
    }

    return (
        <Stack direction='column' spacing={3}>
            {showNSmartButton && (
                <NitrogenSmartFloatingButton
                    cropMonitoringId={cropMonitoring?.id}
                    showNew={cropMonitoring?.nitrogenRecomendations.length === 0}
                    vegetativeStage={cropData?.vegetativeStage}
                    phenology={cropData?.phenology}
                    nitrogenRecommendations={cropMonitoring?.nitrogenRecomendations}
                />
            )}
            {isCMLoading || fieldQuery.isValidating ? (
                <Grid container justifyContent='center' alignItems='center' item xs={12} sx={{ height: '100%' }}>
                    <Spinner />
                </Grid>
            ) : (
                <FieldMonitoringDetail
                    fieldId={cropMonitoring?.field || fieldId}
                    cropMonitoring={cropMonitoring}
                    showCropProtection={showCropProtection}
                    setShowCropProtection={handleShowCropProtection}
                    setTechnology={setTechnology}
                    technology={technology}
                    showAlert={showAlert}
                    historicalWeather={historicalWeather}
                    loadingWeather={loadingWeather}
                    references={references}
                />
            )}
            <CreateRecommendationModal
                open={newRecommendationModalOpened}
                setOpen={() => setNewRecommendationModalOpened(!newRecommendationModalOpened)}
                selectedField={fieldQuery.data || undefined}
                setSelectedField={(field) => {}}
                userNodeId={fieldQuery.data?.userNode}
                entrySource='field-monitoring'
            />
        </Stack>
    );
};

export default FieldMonitoringDashboardCurrentCampaign;
